import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { PlugZap, Users2 } from 'lucide-react';
import { Panel, Field, FormRow, fmtDate } from './design';

// Each connection to government (and email), how it stands, and what to do when it is down; the PRODA
// B2B device's six-monthly reactivation.
const TONE = (state) => (/refused|failed|overdue|lapse/i.test(state) ? 'text-red-800' : /soon|not set|not recorded|not used/i.test(state) ? 'text-amber-700' : 'text-emerald-700');
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// A failed load keeps the panel on screen with what went wrong and a way to try again. The panel
// used to disappear, which read as "nothing to see here" rather than "this did not load".
function LoadError({ message, onRetry }) {
  return (
    <p className="flex flex-wrap items-center gap-2 py-2 text-[12.5px] text-red-800" role="alert">
      {message}
      <button type="button" className="btn-secondary btn-sm" onClick={onRetry}>Try again</button>
    </p>
  );
}

export function Connections() {
  const [d, setD] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [dev, setDev] = useState(null);
  const load = useCallback(() => {
    setLoadError(null);
    api.get('/aged-care/integrations/health').then((r) => setD(r.data))
      .catch((e) => { setD(null); setLoadError(e.response?.data?.message || 'Could not check the connections'); });
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { const r = await api.put('/aged-care/integrations/b2b-device', dev); setDev(null); toast.success(`Saved. Reactivate by ${fmtDate(r.data.reactivateBy)}`); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the device'); }
  };
  return (
    <Panel title="Connections and what to do when one is down" icon={PlugZap} className="mt-5" bodyClass="px-4 py-3 divide-y divide-slate-100 text-[13px]">
      {loadError ? <LoadError message={loadError} onRetry={load} /> : !d ? (
        <p className="py-2 text-slate-500" aria-busy="true">Checking the connections…</p>
      ) : (d.items || []).map((i) => (
        <div key={i.key} className="py-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium text-slate-800">{i.name}</span>
            <span className={`text-[12.5px] ${TONE(i.state)}`}>{i.state}</span>
          </div>
          <p className="text-[12px] text-slate-600">{i.detail}</p>
          <p className="text-[12px] text-slate-500">If it is down: {i.fallback}</p>
          {i.key === 'b2b_device' && !dev && <button type="button" className="btn-ghost btn-sm" onClick={() => setDev({ name: '', activatedOn: isoToday() })}>Record activation</button>}
          {i.key === 'b2b_device' && dev && (
            <div className="flex flex-wrap items-end gap-2 mt-1">
              <Field label="Device name">{(id) => <input id={id} className="input-field !w-48" value={dev.name} onChange={(e) => setDev((p) => ({ ...p, name: e.target.value }))} />}</Field>
              <Field label="Activated on">{(id) => <input id={id} type="date" className="input-field !w-auto" value={dev.activatedOn} onChange={(e) => setDev((p) => ({ ...p, activatedOn: e.target.value }))} />}</Field>
              <button type="button" className="btn-ghost btn-sm" onClick={() => setDev(null)}>Cancel</button>
              <button type="button" className="btn-primary btn-sm" onClick={save}>Save</button>
            </div>
          )}
        </div>
      ))}
    </Panel>
  );
}

// The award each pay group is under, the SCHADS stream of each service, each worker's hours by
// stream and classification per stream. Records only: pay is still worked out from the pay group.
const STREAM = { home_care_aged: 'Home care, aged care', home_care_disability: 'Home care, disability', sacs: 'Social and community services', other: 'Other', unmapped: 'Not set' };
const AWARDS = ['SCHADS', 'Aged Care Award', 'Enterprise agreement', 'Other'];
const streamLabel = (k) => STREAM[k] || 'Other';
export function WorkforceStreams({ className = 'mt-5' }) {
  const [d, setD] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [cls, setCls] = useState(null);
  const load = useCallback(() => {
    setLoadError(null);
    api.get('/aged-care/workforce/streams').then((r) => setD(r.data))
      .catch((e) => { setD(null); setLoadError(e.response?.data?.message || 'Could not load the awards and streams'); });
  }, []);
  useEffect(() => { load(); }, [load]);
  const setStream = async (s, stream) => {
    try { await api.put(`/aged-care/workforce/service-stream/${s.id}`, { stream: stream || null }); toast.success('Saved'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the stream'); }
  };
  const setAward = async (g, award) => {
    try { await api.put(`/aged-care/workforce/pay-group-award/${g.id}`, { award }); toast.success('Saved'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the award'); }
  };
  const saveCls = async () => {
    try {
      await api.put('/aged-care/workforce/worker-stream', { userId: cls.userId, stream: cls.stream, level: cls.level, payPoint: cls.payPoint, effectiveFrom: cls.effectiveFrom });
      setCls(null); toast.success('Saved'); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the classification'); }
  };
  // A classification is kept per stream, so the form opens on the worker's existing one and switching
  // stream brings up what is recorded for that stream (or blanks, for a stream with none yet).
  const fill = (list, stream) => {
    const c = list.find((x) => x.stream === stream);
    return { stream, level: c?.level || '', payPoint: c?.pay_point || '', effectiveFrom: c?.effective_from || '' };
  };
  const openCls = (w) => {
    const list = w.classifications || [];
    const stream = list[0]?.stream || Object.keys(w.byStream || {}).find((k) => (d.streams || []).includes(k)) || 'home_care_aged';
    setCls({ userId: w.id, name: w.name, list, ...fill(list, stream) });
  };

  return (
    <Panel title="Awards and SCHADS streams" icon={Users2} className={className} bodyClass="px-4 py-3 space-y-3 text-[13px]">
      <p className="text-[12px] text-slate-500">
        Records for payroll: which award each pay group is under, which SCHADS stream each service is (Support at Home and CHSP are home care, aged care; NDIS in-home is home care, disability),
        and each worker's classification in each stream they work in. Pay is still worked out from the pay group: how to pay a visit mixing streams waits on Fair Work's answer.
      </p>
      {loadError ? <LoadError message={loadError} onRetry={load} /> : !d ? (
        <p className="text-slate-500" aria-busy="true">Loading the awards and streams…</p>
      ) : (
        <>
          <div>
            <p className="font-medium text-slate-800">Services</p>
            {d.services.length === 0 && <p className="text-slate-500">No active services.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {d.services.map((s) => (
                <label key={s.id} className="flex items-center justify-between gap-2 text-[12.5px]">
                  <span className="truncate text-slate-700">{s.name}</span>
                  <select aria-label={`Stream for ${s.name}`} className="input-field !w-auto !py-0.5" value={s.schads_stream || ''} onChange={(e) => setStream(s, e.target.value)}>
                    <option value="">Not set</option>{d.streams.map((k) => <option key={k} value={k}>{streamLabel(k)}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="font-medium text-slate-800">Pay groups</p>
            {d.payGroups.length === 0 && <p className="text-slate-500">No pay groups set up.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {d.payGroups.map((g) => (
                <label key={g.id} className="flex items-center justify-between gap-2 text-[12.5px]">
                  <span className="truncate text-slate-700">{g.name}</span>
                  <select aria-label={`Award for ${g.name}`} className="input-field !w-auto !py-0.5" value={g.award} onChange={(e) => setAward(g, e.target.value)}>
                    {g.award && !AWARDS.includes(g.award) && <option value={g.award}>{g.award}</option>}
                    {AWARDS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="font-medium text-slate-800">Hours by stream, {fmtDate(d.from)} to {fmtDate(d.to)}</p>
            {d.workers.length === 0 && <p className="text-slate-500">No delivered visits in the period.</p>}
            {d.workers.map((w) => (
              <div key={w.id} className="flex flex-wrap items-baseline justify-between gap-2 py-0.5">
                <span className="text-slate-700">{w.name || 'Worker'}: {Object.entries(w.byStream).map(([k, h]) => `${streamLabel(k)} ${h} h`).join(' · ')}
                  {w.mixed ? <span className="text-amber-800"> · {w.mixed} visit{w.mixed === 1 ? '' : 's'} mixing NDIS and aged care</span> : null}
                  {w.classifications.length ? <span className="text-slate-500"> · {w.classifications.map((c) => `${streamLabel(c.stream)}: level ${c.level || 'not set'}${c.pay_point ? `, pay point ${c.pay_point}` : ''}`).join('; ')}</span> : null}</span>
                {w.streams > 1 && cls?.userId !== w.id && (
                  <button type="button" className="btn-ghost btn-sm" onClick={() => openCls(w)}>{w.classifications.length ? 'Edit classification' : 'Set classification'}</button>
                )}
              </div>
            ))}
            {cls && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2 mt-1">
                <p className="text-slate-700">{cls.name || 'Worker'}</p>
                <FormRow cols={4}>
                  <Field label="Stream">{(id) => <select id={id} className="input-field" value={cls.stream} onChange={(e) => { const v = e.target.value; setCls((p) => ({ ...p, ...fill(p.list, v) })); }}>{d.streams.map((k) => <option key={k} value={k}>{streamLabel(k)}</option>)}</select>}</Field>
                  <Field label="Level">{(id) => <input id={id} className="input-field" value={cls.level} onChange={(e) => setCls((p) => ({ ...p, level: e.target.value }))} />}</Field>
                  <Field label="Pay point">{(id) => <input id={id} className="input-field" value={cls.payPoint} onChange={(e) => setCls((p) => ({ ...p, payPoint: e.target.value }))} />}</Field>
                  <Field label="From">{(id) => <input id={id} type="date" className="input-field" value={cls.effectiveFrom} onChange={(e) => setCls((p) => ({ ...p, effectiveFrom: e.target.value }))} />}</Field>
                </FormRow>
                <div className="flex justify-end gap-2"><button type="button" className="btn-ghost btn-sm" onClick={() => setCls(null)}>Cancel</button><button type="button" className="btn-primary btn-sm" onClick={saveCls}>Save</button></div>
              </div>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}
